import { getModel } from '../../model/registry'
import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import { Wrapper } from '../generic/Layout'
import { Button } from '../generic/Core'
import Table from '../table/Table'
import {
    TableList, TableListItem, TableCell, NoData,
    TableListMobile, TableListItemMobile, CardWrapper, Tags, Flex,
    LeftTag, LeftSubTag, RightTag, RightSubTag, NoDataMobile
} from '../table/Common'
import DisplayElement from '../display/DisplayElement'
import { PopUp } from '../modal/PopUp'
import { SkeletonTable } from '../generic/SkeletonLoader'
import { DeadSpace } from '../mobileNav/MobileNavBar'

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolve a dot-notation path against an object.
 * Supports array index syntax: e.g. "data.locations[0].entries"
 */
function resolvePath(obj, path) {
    if (!obj || !path) return undefined
    return path.split('.').reduce((acc, seg) => {
        if (acc == null) return undefined
        const arrMatch = seg.match(/^(\w+)\[(\d+)\]$/)
        if (arrMatch) {
            const arr = acc[arrMatch[1]]
            return Array.isArray(arr) ? arr[+arrMatch[2]] : undefined
        }
        return acc[seg]
    }, obj)
}

/**
 * Group rows by keyFields and aggregate numeric/boolean fields.
 * aggregateSpec: { fieldName: 'sum' | 'every' | 'any' | 'first' }
 * preserveFields: fields copied from the first row in each group (e.g. display names)
 */
function aggregateRows(rows, keyFields, preserveFields, aggregateSpec) {
    const groups = new Map()
    for (const row of rows) {
        const key = keyFields.map(f => row[f] ?? '').join('::')
        if (!groups.has(key)) {
            const base = {}
            for (const f of keyFields) base[f] = row[f]
            for (const f of preserveFields) base[f] = row[f]
            for (const [f, op] of Object.entries(aggregateSpec)) {
                base[f] = op === 'sum' ? 0 : op === 'every' ? true : op === 'any' ? false : undefined
            }
            groups.set(key, base)
        }
        const g = groups.get(key)
        for (const [f, op] of Object.entries(aggregateSpec)) {
            if (op === 'sum')   g[f] = (g[f] || 0) + (Number(row[f]) || 0)
            if (op === 'every') g[f] = g[f] && !!row[f]
            if (op === 'any')   g[f] = g[f] || !!row[f]
            if (op === 'first' && g[f] === undefined) g[f] = row[f]
        }
    }
    return [...groups.values()]
}

// ── Styled components ─────────────────────────────────────────────────────────

const StatusRow = styled(TableListItem)`
    background-color: ${({ $status, theme }) =>
        $status === 'error' ? theme.emotive.error.bg : 'transparent'};
    cursor: pointer;
    &:hover {
        background-color: ${({ $status, theme }) =>
            $status === 'error' ? theme.emotive.error.bgHover : theme.table.active};
    }
`

const DetailSection = styled.div`
    background: ${({ theme }) => theme.table.childBackground || 'rgba(0,0,0,0.02)'};
    border-top: 1px solid ${({ theme }) => theme.table.borderTop};
`

const DetailHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 0.25rem 2.5rem;
    font-size: var(--font-XXS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    opacity: 0.6;
    border-bottom: 1px solid ${({ theme }) => theme.table.borderTop};
`

const DetailHeaderCell = styled.div`
    flex: 0 1 ${({ $len }) => $len || 'auto'};
    min-width: 0;
    margin-right: 24px;
`

const DetailRow = styled.div`
    display: flex;
    align-items: center;
    padding: 0.6rem 2.5rem;
    border-top: 1px solid ${({ theme }) => theme.table.borderTop};
    color: ${({ theme }) => theme.font.color};
    font-size: var(--font-XS);
`

const SubStatusRow = styled(DetailRow)`
    cursor: pointer;
    background-color: ${({ $status, theme }) =>
        $status === 'error' ? theme.emotive.error.bg : 'transparent'};
    &:hover {
        background-color: ${({ $status, theme }) =>
            $status === 'error' ? theme.emotive.error.bgHover : theme.table.active};
    }
`

const DetailCell = styled.div`
    flex: 0 1 ${({ $len }) => $len || 'auto'};
    min-width: 0;
    margin-right: 24px;
    overflow: hidden;
    p {
        margin: 0;
        font-size: var(--font-XS);
        line-height: 1.4;
    }
`

const ExpandIndicator = styled.span`
    margin-right: 0.5rem;
    font-size: var(--font-XXS);
    opacity: 0.6;
`

const LoadingDetail = styled.div`
    padding: 0.75rem 2.5rem;
    font-size: var(--font-XS);
    opacity: 0.6;
    font-style: italic;
`

const EmptyDetail = styled(LoadingDetail)``

const EntityLink = styled(Link)`
    color: ${({ theme }) => theme.font.link || theme.btn.primary};
    text-decoration: none;
    font-size: var(--font-XS);
    font-weight: 600;
    &:hover { text-decoration: underline; }
`

const MobileStatusDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 0.5rem;
    flex-shrink: 0;
    background-color: ${({ $status, theme }) =>
        $status === 'error' ? theme.emotive.error.color : theme.emotive.success.color};
`

const MobileL2Item = styled(TableListItemMobile)`
    padding-left: 3.5rem;
`

// ── Cell renderers ────────────────────────────────────────────────────────────

/**
 * Render a level2 cell. Handles the 'entityLink' type by building a route
 * from level2.entityRoutes[entry[col.entityField]] + '/' + entry[col.idField].
 */
const renderDetailCell = (col, entry, entityRoutes) => {
    if (col.type === 'entityLink') {
        const entityName = entry[col.entityField]
        const docId = entry[col.idField]
        const basePath = entityRoutes?.[entityName]
        const label = entry[col.id] ?? docId
        if (basePath && docId) {
            return <EntityLink to={`${basePath}/${docId}`} onClick={e => e.stopPropagation()}>{label}</EntityLink>
        }
        return <span>{label}</span>
    }
    return <DisplayElement string={entry[col.id]} type={col.type} col={col} item={entry} />
}

// ── DrillDownView ─────────────────────────────────────────────────────────────

const DrillDownView = ({ type }) => {
    const config = getModel('drillDownViewData')[type]
    const { postMe } = useApi()
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    // expandedL0Key: which level0 row is open (3-level mode only)
    // expandedL1Key: which level1 row is open (both modes — drives level2 fetch)
    const [expandedL0Key, setExpandedL0Key] = useState(null)
    const [expandedL1Key, setExpandedL1Key] = useState(null)
    const [pendingAction, setPendingAction] = useState(null)
    const [showConfirm, setShowConfirm] = useState(false)

    if (!config) return <span>DrillDownView: no config found for "{type}"</span>

    const { source, level0, level1, level2, actions = [] } = config
    const hasLevel0 = !!level0

    const getL0RowKey = (row) => (level0?.keyFields ?? []).map(f => row[f] ?? '').join('::')
    const getL1RowKey = (row) => level1.keyFields.map(f => row[f] ?? '').join('::')

    // ── Source data ───────────────────────────────────────────────────────────

    const sourceQueryKey = ['drillDown', type, source.entity, source.action, source.id]

    const sourceQuery = useQuery({
        queryKey: sourceQueryKey,
        queryFn: () => postMe(`${source.entity}/${source.id}/${source.action}`, {}),
        refetchOnMount: true,
        staleTime: 0,
    })

    const allL1Rows = resolvePath(sourceQuery.data, source.rowsPath) ?? []

    // ── Level 0 rows (derived client-side from source data) ───────────────────

    const level0Rows = hasLevel0
        ? aggregateRows(allL1Rows, level0.keyFields, level0.preserveFields ?? [], level0.aggregate ?? {})
        : []

    // ── Visible level1 rows ───────────────────────────────────────────────────
    // 2-level mode: all source rows
    // 3-level mode: source rows matching the expanded level0 row

    const expandedL0Row = hasLevel0 && expandedL0Key
        ? level0Rows.find(r => getL0RowKey(r) === expandedL0Key)
        : null

    const visibleL1Rows = hasLevel0
        ? (expandedL0Row ? allL1Rows.filter(r => level0.keyFields.every(f => String(r[f] ?? '') === String(expandedL0Row[f] ?? ''))) : [])
        : allL1Rows

    // ── Level 2 data (lazy — only fetched when a level1 row is expanded) ──────

    const expandedL1Row = expandedL1Key
        ? allL1Rows.find(r => getL1RowKey(r) === expandedL1Key)
        : null

    const level2QueryKey = ['drillDown', type, level2.entity, level2.action, expandedL1Key]

    const level2Query = useQuery({
        queryKey: level2QueryKey,
        queryFn: () => {
            const id = expandedL1Row[level2.idField]
            const body = Object.fromEntries(
                Object.entries(level2.paramsFromRow).map(([k, v]) => [k, expandedL1Row[v]])
            )
            return postMe(`${level2.entity}/${id}/${level2.action}`, body)
        },
        enabled: !!expandedL1Row,
        staleTime: 0,
    })

    const level2Rows = expandedL1Row && level2Query.isSuccess
        ? (resolvePath(level2Query.data, level2.rowsPath) ?? [])
        : []

    // ── Toolbar actions ───────────────────────────────────────────────────────

    const actionMutation = useMutation({
        mutationFn: (action) =>
            postMe(`${action.entity}/${action.id}/${action.action}`, {}),
        onSuccess: (_, action) => {
            if (action.onComplete === 'refresh') {
                queryClient.invalidateQueries({ queryKey: sourceQueryKey })
                setExpandedL0Key(null)
                setExpandedL1Key(null)
            }
        },
    })

    const handleAction = (action) => {
        if (action.confirm) {
            setPendingAction(action)
            setShowConfirm(true)
        } else {
            actionMutation.mutate(action)
        }
    }

    const handleModalAction = ({ action }) => {
        setShowConfirm(false)
        if (action === 'confirm' && pendingAction) {
            actionMutation.mutate(pendingAction)
            setPendingAction(null)
        } else {
            setPendingAction(null)
        }
    }

    const confirmModalConfig = pendingAction ? getModel('modalDialogData')[pendingAction.modalConfig] : null

    // ── Row click handlers ────────────────────────────────────────────────────

    const handleL0Click = (row) => {
        const key = getL0RowKey(row)
        setExpandedL0Key(prev => prev === key ? null : key)
        setExpandedL1Key(null)
    }

    const handleL1Click = (row) => {
        const key = getL1RowKey(row)
        setExpandedL1Key(prev => prev === key ? null : key)
    }

    // ── Level 2 detail renderer (shared between 2-level and 3-level modes) ────

    const renderLevel2Detail = () => {
        if (level2Query.isLoading) return <LoadingDetail>Loading entries…</LoadingDetail>
        if (level2Query.isSuccess && level2Rows.length === 0) return <EmptyDetail>No ledger entries found.</EmptyDetail>
        if (level2Query.isSuccess && level2Rows.length > 0) return (
            <>
                <DetailHeader>
                    {level2.columns.map((col, ci) => (
                        <DetailHeaderCell key={ci} $len={col.width}>{t(col.label ?? col.name)}</DetailHeaderCell>
                    ))}
                </DetailHeader>
                {level2Rows.map((entry, ei) => (
                    <DetailRow key={ei}>
                        {level2.columns.map((col, ci) => (
                            <DetailCell key={ci} $len={col.width}>
                                <p>{renderDetailCell(col, entry, level2.entityRoutes)}</p>
                            </DetailCell>
                        ))}
                    </DetailRow>
                ))}
            </>
        )
        return null
    }

    // ── Level 1 row renderer (shared between 2-level and 3-level modes) ───────

    const renderL1Row = (row, i) => {
        const key = getL1RowKey(row)
        const isExpanded = expandedL1Key === key
        const rawStatus = row[level1.statusField]
        const status = level1.statusMap?.[String(rawStatus)] ?? 'ok'

        const cells = level1.columns.map((col, j) => (
            <DetailCell key={j} $len={col.width}>
                <p>
                    <DisplayElement string={row[col.id]} type={col.type} col={col} item={row} />
                </p>
            </DetailCell>
        ))

        return (
            <div key={key || i}>
                <SubStatusRow $status={status} onClick={() => handleL1Click(row)}>
                    <DetailCell $len='20px'>
                        <ExpandIndicator>{isExpanded ? '▼' : '▶'}</ExpandIndicator>
                    </DetailCell>
                    {cells}
                </SubStatusRow>
                {isExpanded && (
                    <DetailSection>{renderLevel2Detail()}</DetailSection>
                )}
            </div>
        )
    }

    // ── Mobile helpers ────────────────────────────────────────────────────────

    // Accept either a bare field-id string (legacy) or { field } object (canonical)
    const getFieldId = tag => (typeof tag === 'object' ? tag?.field : tag)

    // Render a standard (non-entityLink) mobile cell using DisplayElement
    const renderMobileCell = (levelCfg, row, tag) => {
        const fieldId = getFieldId(tag)
        if (!fieldId) return null
        const col = levelCfg.columns.find(c => c.id === fieldId)
        return <DisplayElement string={row[fieldId]} type={col?.type} col={col} item={row} />
    }

    // Render a level2 mobile cell — delegates to renderDetailCell to handle entityLink
    const renderMobileL2Cell = (tag, entry) => {
        const fieldId = getFieldId(tag)
        if (!fieldId) return null
        const col = level2.columns.find(c => c.id === fieldId)
        if (!col) return null
        return renderDetailCell(col, entry, level2.entityRoutes)
    }

    // Level2 rows rendered as compact mobile items (shared between 2-level and 3-level)
    const renderMobileLevel2Detail = () => {
        const mob = level2.mobile ?? {}
        if (level2Query.isLoading) return <MobileL2Item><p style={{ opacity: 0.6, fontStyle: 'italic' }}>Loading…</p></MobileL2Item>
        if (level2Query.isSuccess && level2Rows.length === 0) return <MobileL2Item><p style={{ opacity: 0.6 }}>No entries found.</p></MobileL2Item>
        if (level2Query.isSuccess && level2Rows.length > 0) return level2Rows.map((entry, ei) => (
            <MobileL2Item key={ei}>
                <CardWrapper>
                    <Tags>
                        <LeftTag>{renderMobileL2Cell(mob?.left?.tag, entry)}</LeftTag>
                        {mob?.left?.subTag && <LeftSubTag>{renderMobileL2Cell(mob?.left?.subTag, entry)}</LeftSubTag>}
                    </Tags>
                    <Tags>
                        <RightTag>{renderMobileL2Cell(mob?.right?.tag, entry)}</RightTag>
                        {mob?.right?.subTag && <RightSubTag>{renderMobileL2Cell(mob?.right?.subTag, entry)}</RightSubTag>}
                    </Tags>
                </CardWrapper>
            </MobileL2Item>
        ))
        return null
    }

    // ── Render ────────────────────────────────────────────────────────────────

    // Main header columns depend on mode
    const mainColumns = hasLevel0 ? level0.columns : level1.columns

    return (
        <>
            <Wrapper>
                <h2 style={{ marginBottom: '1rem' }}>{t(config.title)}</h2>
                <div>
                    {actions.map((action, i) => (
                        <Button
                            key={i}
                            $variant='primary'
                            onClick={() => handleAction(action)}
                            disabled={actionMutation.isPending}
                        >
                            {t(action.label)}
                        </Button>
                    ))}
                </div>
            </Wrapper>

            {sourceQuery.isLoading && (
                <Table.Content><SkeletonTable /></Table.Content>
            )}

            {sourceQuery.isError && (
                <Table.Content>
                    <span>Error loading data: {sourceQuery.error?.message}</span>
                </Table.Content>
            )}

            {sourceQuery.isSuccess && (
                <Table.Content>
                    <Table.Header
                        columns={mainColumns}
                        order={[null, 'asc']}
                        sortList={() => {}}
                        lineActions={null}
                        listActions={null}
                        selectAllState='none'
                        onSelectAll={() => {}}
                        selectedCount={0}
                        onListAction={() => {}}
                        selectable={false}
                    />
                    <TableList>
                        {hasLevel0 ? (
                            // ── 3-level render ────────────────────────────────
                            <>
                                {level0Rows.length === 0 && NoData}
                                {level0Rows.map((row, i) => {
                                    const key = getL0RowKey(row)
                                    const isExpanded = expandedL0Key === key
                                    const rawStatus = row[level0.statusField]
                                    const status = level0.statusMap?.[String(rawStatus)] ?? 'ok'

                                    return (
                                        <div key={key || i}>
                                            <StatusRow
                                                $status={status}
                                                onClick={() => handleL0Click(row)}
                                                as='div'
                                            >
                                                <TableCell $len='20px'>
                                                    <ExpandIndicator>{isExpanded ? '▼' : '▶'}</ExpandIndicator>
                                                </TableCell>
                                                {level0.columns.map((col, j) => (
                                                    <TableCell key={j} $len={col.width}>
                                                        <p>
                                                            <DisplayElement
                                                                string={row[col.id]}
                                                                type={col.type}
                                                                col={col}
                                                                item={row}
                                                            />
                                                        </p>
                                                    </TableCell>
                                                ))}
                                            </StatusRow>

                                            {isExpanded && (
                                                <DetailSection>
                                                    {visibleL1Rows.length === 0 ? (
                                                        <EmptyDetail>No locations found.</EmptyDetail>
                                                    ) : (
                                                        <>
                                                            <DetailHeader>
                                                                <DetailHeaderCell $len='20px' />
                                                                {level1.columns.map((col, ci) => (
                                                                    <DetailHeaderCell key={ci} $len={col.width}>
                                                                        {t(col.label ?? col.name)}
                                                                    </DetailHeaderCell>
                                                                ))}
                                                            </DetailHeader>
                                                            {visibleL1Rows.map((l1row, li) => renderL1Row(l1row, li))}
                                                        </>
                                                    )}
                                                </DetailSection>
                                            )}
                                        </div>
                                    )
                                })}
                            </>
                        ) : (
                            // ── 2-level render (backward compat) ─────────────
                            <>
                                {allL1Rows.length === 0 && NoData}
                                {allL1Rows.map((row, i) => {
                                    const key = getL1RowKey(row)
                                    const isExpanded = expandedL1Key === key
                                    const rawStatus = row[level1.statusField]
                                    const status = level1.statusMap?.[String(rawStatus)] ?? 'ok'

                                    return (
                                        <div key={key || i}>
                                            <StatusRow
                                                $status={status}
                                                onClick={() => handleL1Click(row)}
                                                as='div'
                                            >
                                                <TableCell $len='20px'>
                                                    <ExpandIndicator>{isExpanded ? '▼' : '▶'}</ExpandIndicator>
                                                </TableCell>
                                                {level1.columns.map((col, j) => (
                                                    <TableCell key={j} $len={col.width}>
                                                        <p>
                                                            <DisplayElement
                                                                string={row[col.id]}
                                                                type={col.type}
                                                                col={col}
                                                                item={row}
                                                            />
                                                        </p>
                                                    </TableCell>
                                                ))}
                                            </StatusRow>

                                            {isExpanded && (
                                                <DetailSection>{renderLevel2Detail()}</DetailSection>
                                            )}
                                        </div>
                                    )
                                })}
                            </>
                        )}
                    </TableList>

                    {/* ── Mobile cards ─────────────────────────────────────── */}
                    <TableListMobile>
                        {hasLevel0 ? (
                            // ── 3-level mobile ────────────────────────────────
                            <>
                                {level0Rows.length === 0 && NoDataMobile}
                                {level0Rows.map((row, i) => {
                                    const key = getL0RowKey(row)
                                    const isExpanded = expandedL0Key === key
                                    const rawStatus = row[level0.statusField]
                                    const status = level0.statusMap?.[String(rawStatus)] ?? 'ok'
                                    const mob = level0.mobile ?? {}

                                    return (
                                        <Fragment key={key || i}>
                                            <TableListItemMobile onClick={() => handleL0Click(row)}>
                                                <CardWrapper>
                                                    <Tags>
                                                        <Flex>
                                                            <MobileStatusDot $status={status} />
                                                            <LeftTag>{renderMobileCell(level0, row, mob?.left?.tag)}</LeftTag>
                                                        </Flex>
                                                        {mob?.left?.subTag && <LeftSubTag>{renderMobileCell(level0, row, mob?.left?.subTag)}</LeftSubTag>}
                                                    </Tags>
                                                    <Tags>
                                                        <RightTag>{renderMobileCell(level0, row, mob?.right?.tag)}</RightTag>
                                                        {mob?.right?.subTag && <RightSubTag>{renderMobileCell(level0, row, mob?.right?.subTag)}</RightSubTag>}
                                                    </Tags>
                                                </CardWrapper>
                                            </TableListItemMobile>

                                            {isExpanded && visibleL1Rows.map((l1row, li) => {
                                                const l1key = getL1RowKey(l1row)
                                                const l1Expanded = expandedL1Key === l1key
                                                const l1Status = level1.statusMap?.[String(l1row[level1.statusField])] ?? 'ok'
                                                const l1mob = level1.mobile ?? {}

                                                return (
                                                    <Fragment key={l1key || li}>
                                                        <TableListItemMobile $isChild onClick={() => handleL1Click(l1row)}>
                                                            <CardWrapper>
                                                                <Tags>
                                                                    <Flex>
                                                                        <MobileStatusDot $status={l1Status} />
                                                                        <LeftTag>{renderMobileCell(level1, l1row, l1mob?.left?.tag)}</LeftTag>
                                                                    </Flex>
                                                                    {l1mob?.left?.subTag && <LeftSubTag>{renderMobileCell(level1, l1row, l1mob?.left?.subTag)}</LeftSubTag>}
                                                                </Tags>
                                                                <Tags>
                                                                    <RightTag>{renderMobileCell(level1, l1row, l1mob?.right?.tag)}</RightTag>
                                                                    {l1mob?.right?.subTag && <RightSubTag>{renderMobileCell(level1, l1row, l1mob?.right?.subTag)}</RightSubTag>}
                                                                </Tags>
                                                            </CardWrapper>
                                                        </TableListItemMobile>
                                                        {l1Expanded && renderMobileLevel2Detail()}
                                                    </Fragment>
                                                )
                                            })}
                                        </Fragment>
                                    )
                                })}
                            </>
                        ) : (
                            // ── 2-level mobile (backward compat) ─────────────
                            <>
                                {allL1Rows.length === 0 && NoDataMobile}
                                {allL1Rows.map((row, i) => {
                                    const key = getL1RowKey(row)
                                    const isExpanded = expandedL1Key === key
                                    const rawStatus = row[level1.statusField]
                                    const status = level1.statusMap?.[String(rawStatus)] ?? 'ok'
                                    const mob = level1.mobile ?? {}

                                    return (
                                        <Fragment key={key || i}>
                                            <TableListItemMobile onClick={() => handleL1Click(row)}>
                                                <CardWrapper>
                                                    <Tags>
                                                        <Flex>
                                                            <MobileStatusDot $status={status} />
                                                            <LeftTag>{renderMobileCell(level1, row, mob?.left?.tag)}</LeftTag>
                                                        </Flex>
                                                        {mob?.left?.subTag && <LeftSubTag>{renderMobileCell(level1, row, mob?.left?.subTag)}</LeftSubTag>}
                                                    </Tags>
                                                    <Tags>
                                                        <RightTag>{renderMobileCell(level1, row, mob?.right?.tag)}</RightTag>
                                                        {mob?.right?.subTag && <RightSubTag>{renderMobileCell(level1, row, mob?.right?.subTag)}</RightSubTag>}
                                                    </Tags>
                                                </CardWrapper>
                                            </TableListItemMobile>
                                            {isExpanded && renderMobileLevel2Detail()}
                                        </Fragment>
                                    )
                                })}
                            </>
                        )}
                    </TableListMobile>
                </Table.Content>
            )}

            <DeadSpace />

            {showConfirm && confirmModalConfig && (
                <PopUp
                    config={confirmModalConfig}
                    show={showConfirm}
                    setShow={setShowConfirm}
                    setAction={handleModalAction}
                    data={{}}
                />
            )}
        </>
    )
}

export default DrillDownView
